<script>
  import Drawer, { Content, Header, Title, Subtitle } from '@smui/drawer';
  import List, { Item, Text, Graphic, Separator, Subheader } from '@smui/list';
  import Accordion, {
    Panel,
    Header as AccordionHeader,
    Content as AccordionContent,
  } from '@smui-extra/accordion';
  import { Icon } from '@smui/common';
  import InternalBadge from './InternalBadge.svelte';

  import GraphicsListGraphic from './GraphicsListGraphic.svelte';

  import { graphics, selectedProjectObj, selectedOrganizationObj, user } from '../../../stores';

  export let showProjectSettings;
  export let showArticlePreview;
  export let selectedGraphic;
  export let selectedInstance;

  const groupDelimiterRegEx = /.*__.*__/g;
  const titleDelimiter = ' - ';

  $: groupedGraphics = Object.groupBy($graphics, ({ id }) => {
    const matchingString = id.match(groupDelimiterRegEx)?.[0];

    if (matchingString) {
      // remove trailing underscores
      return matchingString.replace(/_+$/, '');
    } else {
      return id;
    }
  });

  $: console.log(groupedGraphics);

  const onGraphicSelect = (graphic) => {
    selectedGraphic = graphic;
    selectedInstance = undefined;
    showProjectSettings = false;
    showArticlePreview = false;
  };
</script>

<div class="graphics-list">
  <Drawer class="graphics-list-drawer" fixed={false}>
    <Header>
      <Title style="margin-bottom: -16px;">Graphics</Title>
      <Subtitle>
        for
        <strong>{$selectedProjectObj.name}</strong>
        <br />
        for
        <strong>{$selectedOrganizationObj.name}</strong></Subtitle>
    </Header>
    <Separator />
    <Content>
      <Accordion>
        {#each Object.entries(groupedGraphics) as [key, items], i}
          {@const firstItem = items[0]}
          {@const hasMultipleGraphics = items.length > 1}
          {@const accordionEnabled = hasMultipleGraphics || firstItem.instances.length > 0}
          {@const sectionLabel =
            hasMultipleGraphics && firstItem.meta_title.includes(' - ')
              ? firstItem.meta_title.slice(0, firstItem.meta_title.lastIndexOf(titleDelimiter))
              : firstItem.meta_title}
          {@const panelOpen =
            accordionEnabled &&
            !!selectedGraphic &&
            !!items.find(({ id }) => id === selectedGraphic.id)}
          <Panel disabled={!accordionEnabled} open={panelOpen}>
            <AccordionHeader>
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="top-level-title"
                on:click={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (panelOpen && (hasMultipleGraphics || firstItem.instances.length > 0)) {
                    onGraphicSelect(undefined);
                  } else {
                    onGraphicSelect(firstItem);
                  }
                }}>
                {#if accordionEnabled}
                  <span class="top-level-title__label">
                    <span class="top-level-title__text">{sectionLabel}</span>
                    {#if !firstItem.ready && !hasMultipleGraphics}
                      <Icon id="list-not-ready" class="material-icons visibility_off"
                        >visibility_off</Icon>
                    {/if}
                    {#if firstItem.internal_only && !hasMultipleGraphics}
                      <InternalBadge />
                    {/if}
                  </span>
                  {#if panelOpen}
                    <Icon class="material-icons">expand_less</Icon>
                  {:else}
                    <Icon class="material-icons">expand_more</Icon>
                  {/if}
                {:else}
                  <GraphicsListGraphic graphic={firstItem} />
                {/if}
              </div>
            </AccordionHeader>

            <AccordionContent>
              <List>
                {#each items as graphic}
                  {#if hasMultipleGraphics}
                    {@const graphicLabel = hasMultipleGraphics
                      ? graphic.meta_title.includes(titleDelimiter)
                        ? graphic.meta_title
                            .slice(graphic.meta_title.lastIndexOf(titleDelimiter) + 2)
                            .trim()
                        : 'Default'
                      : graphic.meta_title.trim()}
                    <Item
                      on:click={() => onGraphicSelect(graphic)}
                      activated={selectedGraphic &&
                        !selectedInstance &&
                        selectedGraphic.graphic_id === graphic.graphic_id}>
                      <GraphicsListGraphic
                        {graphic}
                        label={graphicLabel}
                        onSelect={onGraphicSelect}
                        isParent />
                    </Item>
                  {/if}

                  {#if graphic.instances.length > 0}
                    <Item wrapper>
                      <List class="sub-list">
                        {#each graphic.instances.sort((a, b) => !!b.heading - !!a.heading) as instance, i}
                          <Item
                            activated={selectedInstance &&
                              selectedInstance.graphicinstance_id === instance.graphicinstance_id}
                            on:click={() => {
                              selectedGraphic = graphic;
                              selectedInstance = instance;
                              showProjectSettings = false;
                            }}
                            style="justify-content: space-between;">
                            <Text>{instance.heading || graphic.meta_title}</Text>
                          </Item>
                        {/each}
                      </List>
                    </Item>
                  {/if}
                {/each}
              </List>
            </AccordionContent>
          </Panel>
        {/each}
      </Accordion>
    </Content>
    <Separator />

    <Header class="footer">
      <Item
        href="javascript:void(0)"
        on:click={() => {
          selectedGraphic = undefined;
          selectedInstance = undefined;
          showProjectSettings = true;
          showArticlePreview = false;
        }}
        activated={showProjectSettings}>
        <Graphic class="material-icons" style="margin-right: 10px" aria-hidden="true"
          >settings</Graphic>
        <Text>Project Settings</Text>
      </Item>
      {#if $user?.settings?.is_team}
        <Item
          href="javascript:void(0)"
          on:click={() => {
            selectedGraphic = undefined;
            selectedInstance = undefined;
            showProjectSettings = false;
            showArticlePreview = true;
          }}
          activated={showArticlePreview}>
          <Graphic class="material-icons" style="margin-right: 10px" aria-hidden="true"
            >article</Graphic>
          <Text>Article Preview <span class="team-badge">Team</span></Text>
        </Item>
      {/if}
    </Header>
  </Drawer>
</div>

<style lang="scss">
  :global(#list-not-ready) {
    font-size: 1.5em;
    vertical-align: bottom;
  }

  .graphics-list {
    :global(.sub-list) {
      padding-left: 25px !important;
      padding-top: 0 !important;
      padding-bottom: 0 !important;
    }

    :global(.graphics-list-drawer.mdc-drawer) {
      width: 330px;
    }

    :global(.sub-list > .mdc-deprecated-list-item) {
      font-weight: normal !important;
      padding-top: 3px;
      padding-bottom: 3px;
    }

    :global(.footer.mdc-drawer__header) {
      min-height: 46px;
      padding-bottom: 6px;
    }

    :global(.mdc-deprecated-list-item) {
      height: fit-content !important;
      padding: 8px;
      margin-top: 0px !important;
      margin-bottom: 0px !important;
    }

    :global(.graphic .mdc-deprecated-list-item__text) {
      white-space: break-spaces;
    }

    :global(.smui-paper__content) {
      padding: 0 !important;
    }

    :global(.smui-accordion__header) {
      opacity: 1 !important;
      cursor: pointer !important;
    }

    :global(.smui-accordion__panel) {
      border-top-width: 1px !important;

      &:first-child {
        border: none !important;
      }
    }

    :global(.smui-accordion__header__title) {
      height: fit-content !important;
      padding: 0 !important;
      white-space: break-spaces !important;
      width: 100%;
    }

    .team-badge {
      font-size: 0.65rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      background: #e0e0e0;
      color: #555;
      padding: 1px 5px;
      border-radius: 3px;
      margin-left: 6px;
      vertical-align: middle;
    }

    .top-level-title {
      padding: 8px 8px 8px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;

      &__text {
        font-weight: 900;
        font-size: 14px;
      }

      &__label {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
      }
    }

    :global(.top-level-title .material-icons) {
      font-size: 18px;
    }

    :global(.smui-accordion .smui-accordion__panel.smui-accordion__panel--open) {
      margin: 0 !important;
    }
  }
</style>
